import React from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { formatDateTime } from '@utils/dateTime';
import { Card, Table, Button, Tag, Descriptions, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLoaderData, Link, useRevalidator } from 'react-router-dom';
import ListPageHeader from '@shared/ListPageHeader';
import operatorApiFetch from '@services/operator/apiService';

export default function InventoryOrders() {
  const data = useLoaderData();
  const { t } = useTranslation();
  const revalidator = useRevalidator();

  const statusColors = {
    waiting: 'default',
    pending: 'default',
    processing: 'processing',
    allocated: 'processing',
    shipped: 'success',
    completed: 'success',
    canceled: 'error',
    closed: 'default',
    draft: 'warning',
  };

  const handleCancelOrder = (orderId) => {
    Modal.confirm({
      title: t('common.confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: t('models.order.confirm_cancel'),
      onOk: async () => {
        try {
          const response = await operatorApiFetch(`orders/${orderId}/cancel`, { method: 'POST' });
          if (response.ok) {
            message.success(t('common.success'));
            revalidator.revalidate();
          } else {
            const resData = await response.json();
            message.error(resData.message || t('common.failed'));
          }
        } catch (error) {
          console.error(error);
          message.error(t('common.failed'));
        }
      },
    });
  };

  const columns = [
    {
      title: t('common.type'),
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => (
        <Tag color={type === 'reserved' ? 'orange' : 'blue'}>
          {type === 'reserved'
            ? t('enums.inventory_allocation.type.reserved')
            : t('enums.inventory_allocation.type.allocated')}
        </Tag>
      ),
    },
    {
      title: t('models.order.name'),
      dataIndex: ['source', 'number'],
      key: 'number',
      width: 180,
      render: (text, record) => {
        if (record.source?.type === 'Order') {
           return <Link to={`/orders/${record.source.id}`}>{text}</Link>;
        }
        return text;
      },
    },
    {
      title: t('models.order.receiver_name'),
      dataIndex: ['source', 'customer_name'],
      key: 'customer_name',
      width: 150,
    },
    {
      title: t('models.order.status'),
      dataIndex: ['source', 'status'],
      key: 'status',
      width: 120,
      render: (status, record) => {
        const sourceType = record.source?.type;
        let translatedStatus = status;
        if (sourceType === 'Order') {
          translatedStatus = t(`enums.order.status.${status}`, status);
        } else if (sourceType === 'WorkOrder') {
          translatedStatus = t(`enums.work_order_status.${status}`, status);
        } else {
          // Fallback or other types
          translatedStatus = status;
        }

        return (
          <Tag color={statusColors[status] || 'default'}>
            {translatedStatus}
          </Tag>
        );
      },
    },
    {
      title: t('models.inventory.allocated_quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
    },
    {
      title: t('common.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date) => formatDateTime(date),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_, record) => {
        // Allow cancellation for Reserved orders that are not yet in warehouse processing
        const cancellableStatuses = ['pending', 'allocated', 'out_of_stock'];
        if (record.type === 'reserved' &&
            record.source?.type === 'Order' &&
            cancellableStatuses.includes(record.source?.status)) {
          return (
            <Button
              type="link"
              danger
              onClick={() => handleCancelOrder(record.source.id)}
            >
              {t('common.button.cancel')}
            </Button>
          );
        }
        return null;
      },
    },
  ];

  return (
    <div>
      <ListPageHeader
        items={['stocks', 'stocks.list', { label: data.product_sku }, { label: t('models.inventory.orders_title') }]}
      />

      {/* 庫存資訊 */}
      <Card type="inner" title={t('models.inventory.inventory_info')} style={{ marginBottom: '24px' }}>
        <Descriptions column={3}>
          <Descriptions.Item label={t('models.product.sku')}>
            {data.product_sku || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.product.name')}>
            {data.product_name || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory.batch')}>
            {data.batch || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory.allocated_quantity')}>
            <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
              {data.total_allocated || 0}
            </span>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 佔用列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={data.allocations || []}
          rowKey="id"
          pagination={false}
        />
      </Card>
    </div>
  );
}